import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/client';
import { getDayTranslationKey, saveSelectedDate, getInitialDayOffset } from '../utils/dateUtils';
import { toggleFavourite, useIsFavourite } from '../utils/favourites';
import { useI18n } from '../i18n/I18nContext';
import { Overlay } from './Overlay';
import { useMapContext } from '../contexts/MapContext';
import { usePointWindgram } from '../hooks/usePointWindgram';
import { WindgramView } from './WindgramView';
import { ExternalLinks } from './ExternalLinks';

interface SpotOverlayProps {
  windgramId: string;
}

/**
 * Un sito in elenco. Prima si chiede il windgram al volo nelle coordinate del sito (come
 * sul sito nuovo nei ritagli col cubo dei profili); dove non c'e' - fuori dai ritagli col
 * cubo, o quel giorno il cubo manca: il server lo dice senza addebitare - si mostra la GIF
 * classica di publicwg.php, come prima.
 */
export const SpotOverlay: React.FC<SpotOverlayProps> = ({ windgramId }) => {
  const { t } = useI18n();
  const { findLocationById } = useMapContext();
  const location = findLocationById(windgramId);

  const [windgramUrl, setWindgramUrl] = useState<string>('');
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [selectedDay, setSelectedDay] = useState(() => getInitialDayOffset());
  const isFavourited = useIsFavourite(windgramId);

  const lat = location ? parseFloat(location.coord.lat) : null;
  const lng = location ? parseFloat(location.coord.lng) : null;
  const live = usePointWindgram(lat, lng, selectedDay);
  // la GIF solo quando il windgram al volo ha risposto di no
  const useImage = live.state === 'na' || live.state === 'credit' || live.state === 'error';

  useEffect(() => {
    // Load the persistent day selection when opening overlay
    const initialDay = getInitialDayOffset();
    setSelectedDay(initialDay);
  }, [windgramId]);

  const loadImage = () => {
    setWindgramUrl(''); // Clear previous image immediately
    setImageLoading(true);
    setImageError(false);
    try {
      const result = apiClient.getWindgramUrl(windgramId, selectedDay);
      if (typeof result === 'string') {
        setWindgramUrl(result);
      } else {
        setImageError(true);
        setImageLoading(false);
      }
    } catch (error) {
      console.error('Failed to load windgram:', error);
      setImageError(true);
      setImageLoading(false);
    }
  };

  useEffect(() => {
    if (!location || !useImage) return;
    loadImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, windgramId, selectedDay, useImage]);

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  if (!location || lat === null || lng === null) {
    return (
      <Overlay
        title={t('spot.error')}
        className="spot-error-overlay"
        zIndex={2100}
        alignItems="center"
      >
        <div className="windgram-error">
          <p>{t('spot.notFound')}</p>
          <p>ID: {windgramId}</p>
        </div>
      </Overlay>
    );
  }

  const favouriteButton = (
    <button
      className="favourite-toggle"
      onClick={() => toggleFavourite(windgramId)}
      title={isFavourited ? t('favourites.remove') : t('favourites.add')}
    >
      <img
        src={isFavourited ? "/heart_filled_icon.svg" : "/heart_outline_icon.svg"}
        alt={isFavourited ? t('favourites.remove') : t('favourites.add')}
        width="24"
        height="24"
      />
    </button>
  );

  return (
    <Overlay
      title={location.windgram_name}
      className="spot-overlay"
      actionButtons={favouriteButton}
      zIndex={2100}
      alignItems="center"
    >
      <>
        <div className="day-selector">
          {[0, 1, 2, 3, 4].map((day) => (
            <button
              key={day}
              className={`day-button ${selectedDay === day ? 'day-button-active' : ''}`}
              onClick={() => {
                setSelectedDay(day);
                saveSelectedDate(day); // Persist the selection
              }}
            >
              {t(getDayTranslationKey(day))}
            </button>
          ))}
        </div>

        <div className="forecast-content-container">
          <div className="spot-overlay-content">
            {live.state === 'loading' && (
              <div className="windgram-loading">
                <p>{t('spot.loading')}</p>
              </div>
            )}

            {live.state === 'ok' && <WindgramView data={live.data} name={location.windgram_name} />}

            {useImage && imageLoading && (
              <div className="windgram-loading">
                <p>{t('spot.loading')}</p>
              </div>
            )}

            {useImage && imageError && (
              <div className="windgram-error">
                <p>{t('spot.loadError')}</p>
                <p>{t('spot.coordinates', { lat: location.coord.lat, lng: location.coord.lng })}</p>
                <button onClick={loadImage} className="retry-button">
                  {t('spot.retry')}
                </button>
              </div>
            )}

            {useImage && windgramUrl && !imageError && (
              <img
                src={windgramUrl}
                alt={t('ui.windgramFor', { name: location.windgram_name })}
                className="windgram-image"
                onLoad={handleImageLoad}
                onError={handleImageError}
                style={{ display: imageLoading ? 'none' : 'block' }}
              />
            )}
          </div>

          <ExternalLinks lat={lat} lng={lng} />
        </div>
      </>
    </Overlay>
  );
};
